import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { StepHelpers } from "@shared/components/Stepper";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Progress } from "@shared/ui/progress";
import { Tabs } from "@shared/ui/tabs";
import {
  AlertTriangle,
  BarChart,
  CheckCircle,
  ClipboardList,
  Gauge,
  List,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBillCycleMeterDashboard,
  useBillCycleMeterReadingMissing,
  useBulkBillData,
  useMeterReadingPendingValidation,
  useMeterTypeChoices,
  useUpdateConsumerSelection,
  useUtilityServices,
} from "../../hooks";
import { DashboardTab } from "../meter-readings-tab/DashboardTab";
import { IssuesTab } from "../meter-readings-tab/IssuesTab";
import { MeterListTab } from "../meter-readings-tab/MeterListTab";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

interface MeterReadingsStepProps {
  onSelectionChange?: (selectedRows: any[]) => void;
  // Stepper-injected props
  onNext?: () => void;
  onPrevious?: () => void;
  currentStepIndex?: number;
  stepHelpers?: StepHelpers;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const MeterReadingsStep: React.FC<MeterReadingsStepProps> = ({
  onSelectionChange,
  onNext,
  onPrevious,
  currentStepIndex = 2,
  stepHelpers,
  setValidationError,
  clearValidationError,
  isValidating,
  isFirstStep,
  isLastStep,
}) => {
  const { id, action = "add" } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMissingReadings, setSelectedMissingReadings] = useState<
    string[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [currentPageMissingMeterReading, setCurrentPageMissingMeterReading] =
    useState(1);
  const [validationTableCurrentPage, setValidationTableCurrentPage] =
    useState(1);
  const { data: bulkBillData } = useBulkBillData(id);
  const { mutateAsync: updateConsumer, isPending: isUpdating } =
    useUpdateConsumerSelection(stepHelpers.getStepData(0)?.draftId || id);
  const [selectedMeters, setSelectedMeters] = useState<string[]>([]);
  console.log(
    "stepHelpers.getStepData(0)?.draftId ",
    stepHelpers.getStepData(0)?.draftId,
  );
  // Handler for meter selection change (shared between steps)
  const handleMeterSelectionChange = useCallback((selectedRows: any[]) => {
    setSelectedMeters(selectedRows);
  }, []);
  const draftID = stepHelpers.getStepData(0)?.draftId || id;
  // Helper functions for stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data:", error);
      }
    },
    [stepHelpers, currentStepIndex],
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Get bill cycle ID from previous step
  const getBillCycleId = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return (
        stepHelpers.getDataFromStep(0, "billCycleId") ||
        stepHelpers.getDataFromStep(0, "workflowBillCycleId") ||
        bulkBillData?.billScheduleData?.id
      );
    } catch (error) {
      console.error("Failed to get bill cycle ID:", error);
      return null;
    }
  }, [stepHelpers, bulkBillData]);

  const billCycleId = useMemo(() => {
    return getBillCycleId();
  }, [stepHelpers, bulkBillData]);

  // API hooks (using billCycleId instead of params?.id)
  const {
    data: meterTypeChoice,
    isPending: ismeterTypeChoiceLoading,
    error: meterTypechoiceError,
  } = useMeterTypeChoices();
  const billDraftId = stepHelpers.getStepData(0)?.draftId || id;
  const {
    data: utilityServiceChoice,
    isPending: utilityServiceLoading,
    error: utilityServiceError,
  } = useUtilityServices();

  const {
    data: meterReadingMissingList,
    isPending: isMeterReadingMissingLoading,
    error: meterReadingMissingError,
  } = useBillCycleMeterReadingMissing(getBillCycleId(), billDraftId, 1);

  const {
    data: billCycleMeterDashboard,
    isPending: isBillCycleMeterDashboardLoading,
    error: billCycleMeterDashboardError,
  } = useBillCycleMeterDashboard(getBillCycleId());

  const { data: meterReadingPendingValidationList } =
    useMeterReadingPendingValidation(getBillCycleId(), billDraftId, 1);

  const dataLoadedRef = useRef(false);

  // Load saved data from stepHelpers on component mount
  useEffect(() => {
    if (stepHelpers) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          // Restore state from saved data
          if (savedStepData.activeTab) {
            setActiveTab(savedStepData.activeTab);
          }
          if (savedStepData.selectedMissingReadings) {
            setSelectedMissingReadings(savedStepData.selectedMissingReadings);
          }
          if (savedStepData.currentPage) {
            setCurrentPage(savedStepData.currentPage);
          }
        }
      } catch (error) {
        console.error("Failed to load step data:", error);
      }
    }
  }, [stepHelpers, getStepData]);

  // Save state changes to stepHelpers
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const currentData = getStepData() || {};
        const updatedData = {
          ...currentData,
          activeTab,
          selectedMissingReadings,
          currentPage,
          billCycleId,
          lastUpdated: new Date().toISOString(),
        };
        saveStepData(updatedData);
        dataLoadedRef.current = true;
      } catch {
        dataLoadedRef.current = true;
      }
    }
  }, [
    activeTab,
    selectedMissingReadings,
    currentPage,
    billCycleId,
    saveStepData,
    getStepData,
    stepHelpers,
  ]);

  const handleContinue = () => {
    setShowConfirmationDialog(true);
  };

  const handleProceed = async () => {
    setShowConfirmationDialog(false);
    const payload = {
      step: {
        step_working_on: 2,
        total_steps: 5,
      },
      excluded_consumers: selectedMeters,
    };
    const response = await updateConsumer(payload);

    // Save final step data
    const finalStepData = {
      activeTab,
      selectedMissingReadings,
      currentPage,
      billCycleId,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    };

    if (response) {
      saveStepData(finalStepData);
      if (onNext) {
        onNext();
      }
    }
  };

  const handleDraft = async () => {
    const payload = {
      step: {
        step_working_on: 2,
        total_steps: 5,
      },
    };
    const response = await updateConsumer(payload);

    // Save final step data
    const finalStepData = {
      activeTab,
      selectedMissingReadings,
      currentPage,
      billCycleId,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    };

    if (response) {
      saveStepData(finalStepData);
      navigate("/bx/history?tab=bulk&tab1=draft");
      await queryClient.refetchQueries({
        queryKey: [QueryKeyFactory.module.bx.billHistory.BulkBillDraft(), 1],
        exact: false,
      });
    }
  };

  const handleCancel = () => {
    setShowConfirmationDialog(false);
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "Validated":
        return (
          <Badge className="bg-green-500 hover:bg-green-600">Validated</Badge>
        );
      case "Pending Validation":
        return (
          <Badge className="bg-amber-500 hover:bg-amber-600">
            Pending Validation
          </Badge>
        );
      case "Missing Reading":
        return (
          <Badge variant="outline" className="border-red-500 text-red-500">
            Missing Reading
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const tabComponents = {
    dashboard: {
      label: "Dashboard",
      icon: <BarChart className="h-4 w-4" />,
      component: <DashboardTab billCycleId={billCycleId} />,
    },
    "meter-list": {
      label: "Meter Reading List",
      icon: <List className="h-4 w-4" />,
      component: (
        <MeterListTab billCycleId={billCycleId} billDraftId={billDraftId} />
      ),
    },
    issues: {
      label: "Issues",
      icon: <AlertTriangle className="h-4 w-4" />,
      count:
        (meterReadingMissingList?.count || 0) +
        (meterReadingPendingValidationList?.count || 0),
      component: (
        <IssuesTab
          billCycleId={billCycleId}
          billDraftId={billDraftId}
          onSelectionChange={handleMeterSelectionChange}
        />
      ),
    },
  };

  const meterKpiCards = [
    {
      title: "Total Meters",
      count: billCycleMeterDashboard?.totalMeters ?? "NA",
      trend: billCycleMeterDashboard?.currentCycleReadingPercentage ?? "NA",
      tooltip: "Meters in current cycle",
      icon: <Gauge className="h-8 w-8 text-sky-500" />,
    },
    {
      title: "Readings Collected",
      count: `${billCycleMeterDashboard?.readingCollectedPercentage ?? "NA"}%`,
      tooltip: "Progress of collection",
      icon: <ClipboardList className="h-8 w-8 text-green-600" />,
    },
    {
      title: "Missing Readings",
      count: billCycleMeterDashboard?.pendingReadingCount ?? "NA",
      trend: billCycleMeterDashboard?.pendingReadingPercentage ?? "NA",
      tooltip: "Meters without readings",
      icon: <AlertTriangle className="h-8 w-8 text-amber-500" />,
    },
    {
      title: "Pending Validation",
      count: billCycleMeterDashboard?.pendingValidationCount ?? "NA",
      trend: billCycleMeterDashboard?.pendingValidationPercentage ?? "NA",
      tooltip: "Readings to be validated",
      icon: <CheckCircle className="h-8 w-8 text-purple-500" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="page-subheading">Meter Readings Analysis</h3>
        <p className="page-subtitle">
          Analyze meter readings, validate data, and resolve issues
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102 focus-within:shadow-2xl focus-within:ring-2 focus-within:ring-accent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="text-muted-foreground text-sm font-medium">
                Total Meters
              </div>
              <div className="text-sm text-muted-foreground">
                Meters in current cycle
              </div>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold">
                  {billCycleMeterDashboard?.totalMeters ?? "NA"}
                </span>
                <span className="ml-2 text-sm text-green-500">
                  (
                  {billCycleMeterDashboard?.currentCycleReadingPercentage ??
                    "NA"}
                  %)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102 focus-within:shadow-2xl focus-within:ring-2 focus-within:ring-accent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="text-muted-foreground text-sm font-medium">
                Readings Collected
              </div>
              <div className="text-sm text-muted-foreground">
                Progress of collection
              </div>
              <div className="text-3xl font-bold">
                {billCycleMeterDashboard?.readingCollectedPercentage ?? "NA"}%
              </div>
              <Progress
                value={billCycleMeterDashboard?.readingCollectedPercentage ?? 0}
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102 focus-within:shadow-2xl focus-within:ring-2 focus-within:ring-accent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="text-muted-foreground text-sm font-medium">
                Missing Readings
              </div>
              <div className="text-sm text-muted-foreground">
                Meters without readings
              </div>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold">
                  {billCycleMeterDashboard?.pendingReadingCount ?? "NA"}
                </span>
                <span className="ml-2 text-sm text-amber-500">
                  ({billCycleMeterDashboard?.pendingReadingPercentage ?? "NA"}%)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102 focus-within:shadow-2xl focus-within:ring-2 focus-within:ring-accent">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="text-muted-foreground text-sm font-medium">
                Pending Validation
              </div>
              <div className="text-sm text-muted-foreground">
                Readings to be validated
              </div>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold">
                  {billCycleMeterDashboard?.pendingValidationCount ?? "NA"}
                </span>
                <span className="ml-2 text-sm text-amber-500">
                  (
                  {billCycleMeterDashboard?.pendingValidationPercentage ?? "NA"}
                  %)
                </span>
              </div>
            </div>
          </CardContent>
        </Card> */}
        {meterKpiCards.map((meter) => (
          <QuickStatsCard {...meter} />
        ))}
      </div>

      {/* FIXED: Removed urlMapping and idPrefix to prevent URL conflicts */}
      <Tabs
        tabComponents={tabComponents}
        defaultValue="dashboard"
        tabsListClassName="w-full grid grid-cols-3"
        disableUrlSync={true}
      />

      <div className="flex justify-between pt-6">
        <Button onClick={onPrevious} disabled={isFirstStep}>
          Previous
        </Button>
        <div className="flex gap-4">
          <Button onClick={handleDraft}>Save As Draft</Button>
          <Button onClick={handleContinue}>Continue to Bill Generation</Button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmationDialog}
        onOpenChange={setShowConfirmationDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Proceed to Bill Generation
            </DialogTitle>
            <DialogDescription className="text-base">
              All meters with missing readings will be estimated and estimated
              bills will be generated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleProceed}>Proceed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeterReadingsStep;
